import { storageService } from '../utils/localFileService.js';

/**
 * ✅ NEW: Scheduled PDF Cleanup Service
 * Handles automatic cleanup of PDF files older than 20 days
 * Includes error handling, retry logic, and monitoring capabilities
 */
class ScheduledCleanupService {
  
  constructor() {
    this.isRunning = false;
    this.lastRunDate = null;
    this.nextRunDate = null;
    this.cleanupInterval = null;
    this.retryAttempts = 0;
    this.maxRetryAttempts = 3;
    
    // Run cleanup every 24 hours (86400000 milliseconds)
    this.cleanupIntervalMs = 24 * 60 * 60 * 1000;
    
    // Initialize cleanup schedule on service creation
    this.initializeCleanupSchedule();
  }

  /**
   * Initialize automatic cleanup schedule
   * Starts the periodic cleanup process
   */
  initializeCleanupSchedule() {
    try {
      console.log('🔄 Initializing PDF cleanup schedule (every 24 hours)...');
      
      // Run initial cleanup after 30 seconds
      setTimeout(() => {
        this.runScheduledCleanup();
      }, 30000);
      
      // Set up periodic cleanup (every 24 hours)
      this.cleanupInterval = setInterval(() => {
        this.runScheduledCleanup();
      }, this.cleanupIntervalMs);
      
      this.nextRunDate = new Date(Date.now() + this.cleanupIntervalMs);
      
      console.log(`✅ PDF cleanup scheduled. Next run: ${this.nextRunDate?.toLocaleString()}`);
      
    } catch (error) {
      console.error('Error initializing cleanup schedule:', error);
    }
  }

  /**
   * Execute scheduled cleanup with retry logic
   */
  async runScheduledCleanup() {
    if (this.isRunning) {
      console.log('⏳ Cleanup already running, skipping...');
      return;
    }

    this.isRunning = true;
    this.lastRunDate = new Date();
    
    try {
      console.log('🧹 Starting scheduled PDF cleanup...');
      
      // Get preview first to see what would be deleted
      const previewResult = await storageService?.getCleanupPreview();
      
      if (!previewResult?.success) {
        throw new Error(`Preview failed: ${previewResult?.error}`);
      }
      
      const { preview } = previewResult;
      
      if (preview?.filesToDelete === 0) {
        console.log('✅ No PDF files older than 20 days found. Storage is clean.');
        this.retryAttempts = 0;
        this.nextRunDate = new Date(Date.now() + this.cleanupIntervalMs);
        return;
      }
      
      console.log(`📊 Cleanup preview: ${preview?.filesToDelete} files to delete, ${preview?.totalSizeToFreeMB} MB to free`);
      
      // Execute cleanup
      const cleanupResult = await storageService?.executeAutoCleanup();
      
      if (!cleanupResult?.success) {
        throw new Error(`Cleanup failed: ${cleanupResult?.error}`);
      }
      
      const { cleanup } = cleanupResult;
      
      console.log(`✅ Cleanup completed successfully!`);
      console.log(`   - Files deleted: ${cleanup?.deletedCount}`);
      console.log(`   - Space freed: ${cleanup?.totalSizeFreedMB} MB`);
      console.log(`   - Message: ${cleanup?.message}`);
      
      // Reset retry attempts on success
      this.retryAttempts = 0;
      
      // Schedule next run
      this.nextRunDate = new Date(Date.now() + this.cleanupIntervalMs);
      
    } catch (error) {
      console.error('❌ Scheduled cleanup failed:', error);
      
      this.retryAttempts++;
      
      if (this.retryAttempts < this.maxRetryAttempts) {
        console.log(`🔄 Scheduling retry ${this.retryAttempts}/${this.maxRetryAttempts} in 1 hour...`);
        
        // Retry in 1 hour
        setTimeout(() => {
          this.runScheduledCleanup();
        }, 60 * 60 * 1000);
        
      } else {
        console.error(`💥 Max retry attempts reached. Next cleanup in 24 hours.`);
        this.retryAttempts = 0;
        this.nextRunDate = new Date(Date.now() + this.cleanupIntervalMs);
      }
      
    } finally {
      this.isRunning = false;
    }
  }

  /**
   * Manual cleanup trigger (can be called from UI)
   */
  async runManualCleanup() {
    try {
      console.log('🔧 Running manual PDF cleanup...');
      
      const result = await storageService?.executeAutoCleanup();
      
      if (!result?.success) {
        throw new Error(result?.error || 'Manual cleanup failed');
      }
      
      const { cleanup } = result;
      
      return {
        success: true,
        result: {
          deletedCount: cleanup?.deletedCount || 0,
          totalSizeFreedMB: cleanup?.totalSizeFreedMB || 0,
          message: cleanup?.message || 'Manual cleanup completed'
        }
      };
      
    } catch (error) {
      console.error('Error in manual cleanup:', error);
      return {
        success: false,
        error: error?.message || 'Manual cleanup failed'
      };
    }
  }

  /**
   * Get cleanup status and statistics
   */
  async getCleanupStatus() {
    try {
      // Get storage statistics
      const statsResult = await storageService?.getEnhancedStorageStats();
      const previewResult = await storageService?.getCleanupPreview();
      const historyResult = await storageService?.getCleanupHistory(5);
      
      return {
        success: true,
        status: {
          isRunning: this.isRunning,
          lastRunDate: this.lastRunDate,
          nextRunDate: this.nextRunDate,
          retryAttempts: this.retryAttempts,
          maxRetryAttempts: this.maxRetryAttempts,
          intervalHours: this.cleanupIntervalMs / (1000 * 60 * 60),
          
          // Storage statistics
          storageStats: statsResult?.success ? statsResult?.stats : null,
          cleanupPreview: previewResult?.success ? previewResult?.preview : null,
          recentHistory: historyResult?.success ? historyResult?.history : []
        }
      };
      
    } catch (error) {
      console.error('Error getting cleanup status:', error);
      return {
        success: false,
        error: error?.message || 'Error getting cleanup status'
      };
    }
  }

  /**
   * Stop scheduled cleanup (cleanup method)
   */
  stopScheduledCleanup() {
    try {
      if (this.cleanupInterval) {
        clearInterval(this.cleanupInterval);
        this.cleanupInterval = null;
        console.log('🛑 Scheduled PDF cleanup stopped');
      }
    } catch (error) {
      console.error('Error stopping cleanup:', error);
    }
  }

  /**
   * Restart scheduled cleanup
   */
  restartScheduledCleanup() {
    try {
      this.stopScheduledCleanup();
      this.initializeCleanupSchedule();
      console.log('🔄 Scheduled PDF cleanup restarted');
    } catch (error) {
      console.error('Error restarting cleanup:', error);
    }
  }
}

// ✅ Export singleton instance
export const scheduledCleanupService = new ScheduledCleanupService();

// Export class for testing or multiple instances if needed
export { ScheduledCleanupService };
